import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "../i18n";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";

import { getProductImage, getProductImagePolicy } from "../api/products";

const FALLBACK_SUPPORTED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export default function ProductImageField({
  product,
  selectedFile,
  onSelectFile,
  removeExisting,
  onRemoveExisting,
  disabled = false,
}) {
  const { t } = useTranslation();
  const [remoteUrl, setRemoteUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [policy, setPolicy] = useState(null);
  const localUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : ""),
    [selectedFile],
  );

  useEffect(() => {
    let cancelled = false;
    getProductImagePolicy()
      .then((response) => {
        if (!cancelled) setPolicy(response.data || null);
      })
      .catch(() => {
        if (!cancelled) setPolicy(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => () => {
    if (localUrl) URL.revokeObjectURL(localUrl);
  }, [localUrl]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";
    setRemoteUrl("");
    setError("");
    setLoading(false);
    if (!product?.public_id || !product?.image || removeExisting || selectedFile) {
      return undefined;
    }
    setLoading(true);
    getProductImage(product.public_id)
      .then((response) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(response.data);
        setRemoteUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setError(t("images.loadError"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [product?.public_id, product?.image, removeExisting, selectedFile, t]);

  const previewUrl = localUrl || remoteUrl;
  const hasExisting = Boolean(product?.image && !removeExisting);

  const choose = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const supportedTypes = new Set(
      Array.isArray(policy?.allowed_mime_types)
        ? policy.allowed_mime_types
        : [...FALLBACK_SUPPORTED_TYPES],
    );
    if (!supportedTypes.has(file.type)) {
      setError(t("images.invalidType"));
      return;
    }
    const maximumBytes = Number(policy?.max_bytes || 0);
    if (file.size < 1 || (maximumBytes > 0 && file.size > maximumBytes)) {
      const maximumMb = maximumBytes > 0
        ? `${(maximumBytes / 1_000_000).toFixed(1)} MB`
        : t("images.configuredLimit");
      setError(t("images.tooLarge", { size: maximumMb }));
      return;
    }
    setError("");
    onRemoveExisting(false);
    onSelectFile(file);
  };

  const remove = () => {
    setError("");
    onSelectFile(null);
    if (product?.image) onRemoveExisting(true);
  };

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Box>
          <Typography fontWeight={850}>{t("images.title")}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t("images.help")}
          </Typography>
        </Box>
        {hasExisting && !selectedFile && (
          <Typography variant="caption" color="success.main" fontWeight={800}>
            {t("images.saved")}
          </Typography>
        )}
      </Stack>

      <Box
        sx={{
          minHeight: 180,
          borderRadius: 3,
          border: "1px dashed #bfdbfe",
          bgcolor: "#f8fbff",
          display: "grid",
          placeItems: "center",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {loading ? (
          <CircularProgress size={28} />
        ) : previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={t("images.preview")}
            sx={{ width: "100%", height: 220, objectFit: "contain", bgcolor: "#fff" }}
          />
        ) : (
          <Stack alignItems="center" spacing={0.75} sx={{ p: 3, color: "text.secondary" }}>
            <ImageRoundedIcon sx={{ fontSize: 42, color: "primary.main" }} />
            <Typography fontWeight={800}>{t("images.none")}</Typography>
            <Typography variant="caption">
              JPEG, PNG or WEBP
              {Number(policy?.max_bytes || 0) > 0
                ? t("images.max", { size: (Number(policy.max_bytes) / 1_000_000).toFixed(1) })
                : t("images.serverLimit")}
            </Typography>
          </Stack>
        )}
      </Box>

      {error && <Alert severity="warning">{error}</Alert>}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
        <Button
          component="label"
          variant="outlined"
          startIcon={<AddPhotoAlternateRoundedIcon />}
          disabled={disabled}
        >
          {t(previewUrl || hasExisting ? "images.replace" : "images.upload")}
          <input hidden type="file" accept="image/jpeg,image/png,image/webp" onChange={choose} />
        </Button>
        {(previewUrl || hasExisting) && (
          <Button
            color="error"
            variant="text"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={remove}
            disabled={disabled}
          >
            {t("images.remove")}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
